import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { db } from '../services/firebase';
import { 
  collection, 
  addDoc, 
  getDocs,
  getDoc, 
  doc, 
  deleteDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import styled from 'styled-components';

const FirestoreTestContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const TestItem = styled.li`
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 1rem;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  color: #dc3545;
`;

const ErrorItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid #dc3545;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const TestForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;

  input {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  button {
    padding: 0.5rem;
    background: var(--primary-color);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: #357abd;
    }
  }
`;

const FirestoreTest = () => {
  const { currentUser } = useAuth();
  const [testResults, setTestResults] = useState({
    posts: {
      read: { success: false, error: null },
      write: { success: false, error: null },
      delete: { success: false, error: null }
    },
    categories: {
      read: { success: false, error: null },
      write: { success: false, error: null },
      delete: { success: false, error: null }
    },
    users: {
      read: { success: false, error: null },
      write: { success: false, error: null },
      delete: { success: false, error: null }
    }
  });

  const testPosts = async () => {
    if (!currentUser) {
      setTestResults(prev => ({
        ...prev,
        posts: {
          read: { success: false, error: "User must be authenticated" },
          write: { success: false, error: "User must be authenticated" },
          delete: { success: false, error: "User must be authenticated" }
        }
      }));
      return;
    }

    try {
      // Test read
      const postsSnapshot = await getDocs(collection(db, 'posts'));
      setTestResults(prev => ({
        ...prev,
        posts: { ...prev.posts, read: { success: true, error: null } }
      }));

      // Test write
      const docRef = await addDoc(collection(db, 'posts'), {
        title: 'Test Post',
        content: 'Test Content',
        authorId: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setTestResults(prev => ({
        ...prev,
        posts: { ...prev.posts, write: { success: true, error: null } }
      }));

      // Test delete
      await deleteDoc(doc(db, 'posts', docRef.id));
      setTestResults(prev => ({
        ...prev,
        posts: { ...prev.posts, delete: { success: true, error: null } }
      }));
    } catch (error) {
      console.error('Posts test error:', error);
      setTestResults(prev => ({
        ...prev,
        posts: {
          ...prev.posts,
          [error.code === 'permission-denied' ? 'write' : 'read']: { 
            success: false, 
            error: error.message 
          }
        }
      }));
    }
  };

  const testCategories = async () => {
    if (!currentUser) {
      setTestResults(prev => ({
        ...prev,
        categories: {
          read: { success: false, error: "User must be authenticated" },
          write: { success: false, error: "User must be authenticated" },
          delete: { success: false, error: "User must be authenticated" }
        }
      }));
      return;
    }

    try {
      // Test read
      const categoriesSnapshot = await getDocs(collection(db, 'categories'));
      setTestResults(prev => ({
        ...prev,
        categories: { ...prev.categories, read: { success: true, error: null } }
      }));

      // Test write
      const docRef = await addDoc(collection(db, 'categories'), {
        name: 'Test Category',
        description: 'Test Description',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.uid
      });
      setTestResults(prev => ({
        ...prev,
        categories: { ...prev.categories, write: { success: true, error: null } }
      }));

      // Test delete
      await deleteDoc(doc(db, 'categories', docRef.id));
      setTestResults(prev => ({
        ...prev,
        categories: { ...prev.categories, delete: { success: true, error: null } }
      }));
    } catch (error) {
      console.error('Categories test error:', error);
      setTestResults(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          [error.code === 'permission-denied' ? 'write' : 'read']: { 
            success: false, 
            error: error.message 
          }
        }
      }));
    }
  };

  const testUsers = async () => {
    if (!currentUser) {
      setTestResults(prev => ({
        ...prev,
        users: {
          read: { success: false, error: "User must be authenticated" },
          write: { success: false, error: "User must be authenticated" },
          delete: { success: false, error: "User must be authenticated" }
        }
      }));
      return;
    }

    try {
      // Test read
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        setTestResults(prev => ({
          ...prev,
          users: { ...prev.users, read: { success: true, error: null } }
        }));
      } else {
        throw new Error('User document does not exist');
      }

      // Test write
      await updateDoc(userDocRef, {
        lastTested: serverTimestamp()
      });
      setTestResults(prev => ({
        ...prev,
        users: { ...prev.users, write: { success: true, error: null } }
      }));

      // Note: We don't test delete for users as it's not allowed
      setTestResults(prev => ({
        ...prev,
        users: { ...prev.users, delete: { success: true, error: "Delete operation not tested for users" } }
      }));
    } catch (error) {
      console.error('Users test error:', error);
      setTestResults(prev => ({
        ...prev,
        users: {
          ...prev.users,
          [error.code === 'permission-denied' ? 'write' : 'read']: { 
            success: false, 
            error: error.message 
          }
        }
      }));
    }
  };

  return (
    <FirestoreTestContainer>
      <TestSection>
        <h3>Firestore Security Rules Tests</h3>
        
        <TestForm onSubmit={(e) => { e.preventDefault(); testPosts(); }}>
          <button type="submit">Test Posts Collection</button>
        </TestForm>

        <TestForm onSubmit={(e) => { e.preventDefault(); testCategories(); }}>
          <button type="submit">Test Categories Collection</button>
        </TestForm>

        <TestForm onSubmit={(e) => { e.preventDefault(); testUsers(); }}>
          <button type="submit">Test Users Collection</button>
        </TestForm>

        <TestResults>
          <h3>Test Results</h3>
          <TestList>
            <TestItem>Posts Read: {testResults.posts.read.success ? '✅' : '❌'}</TestItem>
            <TestItem>Posts Write: {testResults.posts.write.success ? '✅' : '❌'}</TestItem>
            <TestItem>Posts Delete: {testResults.posts.delete.success ? '✅' : '❌'}</TestItem>
            <TestItem>Categories Read: {testResults.categories.read.success ? '✅' : '❌'}</TestItem>
            <TestItem>Categories Write: {testResults.categories.write.success ? '✅' : '❌'}</TestItem>
            <TestItem>Categories Delete: {testResults.categories.delete.success ? '✅' : '❌'}</TestItem>
            <TestItem>Users Read: {testResults.users.read.success ? '✅' : '❌'}</TestItem>
            <TestItem>Users Write: {testResults.users.write.success ? '✅' : '❌'}</TestItem>
            <TestItem>Users Delete: {testResults.users.delete.success ? '✅' : '❌'}</TestItem>
          </TestList>

          {(Object.values(testResults).some(collection => 
            Object.values(collection).some(test => test.error)
          )) && (
            <ErrorList>
              {testResults.posts.read.error && <ErrorItem>Posts Read: {testResults.posts.read.error}</ErrorItem>}
              {testResults.posts.write.error && <ErrorItem>Posts Write: {testResults.posts.write.error}</ErrorItem>}
              {testResults.posts.delete.error && <ErrorItem>Posts Delete: {testResults.posts.delete.error}</ErrorItem>}
              {testResults.categories.read.error && <ErrorItem>Categories Read: {testResults.categories.read.error}</ErrorItem>}
              {testResults.categories.write.error && <ErrorItem>Categories Write: {testResults.categories.write.error}</ErrorItem>}
              {testResults.categories.delete.error && <ErrorItem>Categories Delete: {testResults.categories.delete.error}</ErrorItem>}
              {testResults.users.read.error && <ErrorItem>Users Read: {testResults.users.read.error}</ErrorItem>}
              {testResults.users.write.error && <ErrorItem>Users Write: {testResults.users.write.error}</ErrorItem>}
              {testResults.users.delete.error && <ErrorItem>Users Delete: {testResults.users.delete.error}</ErrorItem>}
            </ErrorList>
          )}
        </TestResults>
      </TestSection>
    </FirestoreTestContainer>
  );
};

export default FirestoreTest; 